import { z } from "zod";
import { InventaireCreationSourceSchema } from "./commun.zod";
import { EssenceCreateSchema, EssenceSchema } from "./essence.zod";

export const CoordinateSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

export const PolygonSchema = z.array(CoordinateSchema).min(3, "Un polygone doit contenir au moins trois points.");

export const EpaysageBaseSchema = z.object({
  coord: z.union([PolygonSchema, z.string()]).nullable().optional(),
  address: z.string().max(255, "L'adresse ne peut pas dépasser 255 caractères.").nullable().optional(),
  ville: z.string().max(255, "La ville ne peut pas dépasser 255 caractères.").nullable().optional(),
  pays: z.string().max(255, "Le pays ne peut pas dépasser 255 caractères.").nullable().optional(),
  area: z.number().positive("La superficie doit être positive.").nullable().optional(),
  type_espace: z.string().max(255, "Le type d'espace ne peut pas dépasser 255 caractères.").nullable().optional(),
  type_espace_autre: z.string().max(255, "Le type d'espace autre ne peut pas dépasser 255 caractères.").nullable().optional(),
  code_site: z.string().max(255, "Le code site ne peut pas dépasser 255 caractères.").nullable().optional(),
  num_sujet: z.string().max(255, "Le numéro de sujet ne peut pas dépasser 255 caractères.").nullable().optional(),
  age_estime: z.number().int().nullable().optional(),
  milieu: z.string().max(255, "Le milieu ne peut pas dépasser 255 caractères.").nullable().optional(),
  milieu_other: z.string().max(255, "Le champ autre du milieu ne peut pas dépasser 255 caractères.").nullable().optional(),
  sol: z.string().max(255, "Le sol ne peut pas dépasser 255 caractères.").nullable().optional(),
  sol_other: z.string().max(255, "Le champ autre du sol ne peut pas dépasser 255 caractères.").nullable().optional(),
  domaine: z.string().max(255, "Le domaine ne peut pas dépasser 255 caractères.").nullable().optional(),
  situation: z.string().max(255, "La situation ne peut pas dépasser 255 caractères.").nullable().optional(),
  nuissance: z.any().nullable().optional(), // JSON field
  proximite: z.any().nullable().optional(), // JSON field
  proximite_other: z.string().max(255, "Le champ autre de la proximité ne peut pas dépasser 255 caractères.").nullable().optional(),
  nuissance_other: z.string().max(255, "Le champ autre de la nuisance ne peut pas dépasser 255 caractères.").nullable().optional(),
  type_passage: z.array(z.string().max(255, "Le type de passage ne peut pas dépasser 255 caractères.")).nullable().optional(),
  type_passage_other: z.string().max(255, "Le champ autre du type de passage ne peut pas dépasser 255 caractères.").nullable().optional(),
  taux_freq: z.string().max(255, "Le taux de fréquence ne peut pas dépasser 255 caractères.").nullable().optional(),
  accessibilite: z.string().max(255, "L'accessibilité ne peut pas dépasser 255 caractères.").nullable().optional(),
  accessibilite_other: z.string().max(255, "Le champ autre de l'accessibilité ne peut pas dépasser 255 caractères.").nullable().optional(),
  risque_general: z.string().max(255, "Le risque général ne peut pas dépasser 255 caractères.").nullable().optional(),
  commentaire: z.string().max(255, "Le commentaire ne peut pas dépasser 255 caractères.").nullable().optional(),
  img1: z.string().max(255, "L'URL de l'image 1 ne peut pas dépasser 255 caractères.").nullable().optional(),
  img2: z.string().max(255, "L'URL de l'image 2 ne peut pas dépasser 255 caractères.").nullable().optional(),
  img3: z.string().max(255, "L'URL de l'image 3 ne peut pas dépasser 255 caractères.").nullable().optional(),
  essence: z.array(EssenceSchema).optional(),
  is_alert: z.boolean().nullable().optional(),
  location_index: z.number().int().nullable().optional(),
});

export const EpaysageSchema = EpaysageBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
  updated_at: z.date().nullable().optional(),
});

export const EpaysageCreateSchema = EpaysageBaseSchema.omit({
  essence: true,
});

export const EpaysageUpdateSchema = EpaysageCreateSchema.extend({
  id: z.number().int().positive(),
});

export const EpaysageListResponseSchema = z.object({
  data: z.array(EpaysageSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export const EpaysageResponseSchema = z.object({
  data: EpaysageSchema,
  message: z.string().optional(),
});

export const EpaysageSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  address: z.string().optional(),
  ville: z.string().optional(),
  pays: z.string().optional(),
  code_site: z.string().optional(),
  num_sujet: z.string().optional(),
  domaine: z.string().optional(),
  type_espace: z.string().optional(),
});

export const EpaysageMinimalDataSchema = EpaysageSchema.pick({
  created_at: true,
  updated_at: true,
  essence: true,
  coord: true,
  type_espace: true,
  domaine: true,
  is_alert: true,
});

export const EpaysageDataSchema = EpaysageCreateSchema.extend({
  surface: z.number().positive("La surface doit être positive.").optional(),
  nom_espace: z.string().max(255, "Le nom de l'espace ne peut pas dépasser 255 caractères.").optional(),
  reference: z.string().max(255, "La référence ne peut pas dépasser 255 caractères.").nullable().optional(),
});

export const EssenceDataSchema = EssenceCreateSchema.omit({ epaysage_id: true }).extend({
  id: z.number().int().positive().optional(),
  _essenceId: z.number().int().positive().optional(),
});

export const InventaireDataSchema = z.object({
  is_finished: z.boolean().optional(),
  variety_grade: z.number().nullable().optional(),
  health_index: z.number().int().nullable().optional(),
  aesthetic_index: z.number().int().nullable().optional(),
  location_index: z.number().int().nullable().optional(),
  aesthetic_column: z.number().int().nullable().optional(),
  health_column: z.number().int().nullable().optional(),
  zip_code: z.string().max(100, "Le code postal ne peut pas dépasser 100 caractères").nullable().optional(),
});

export const EpaysageCompleteInputSchema = z.object({
  mode: z.enum(["CREATE", "UPDATE"]),
  inventaire_id: z.number().int().positive().optional(),
  epaysage_id: z.number().int().positive().optional(),
  epaysage: EpaysageDataSchema,
  essences: z.array(EssenceDataSchema).min(1, "Au moins une essence est requise.").max(5, "Cinq essences au maximum sont autorisées."),
  inventaire: InventaireDataSchema.optional(),
  creation_source: InventaireCreationSourceSchema.optional(),
});

export const EpaysageBaseSaveSchema = z.object({
  mode: z.enum(["CREATE", "UPDATE"]),
  epaysage_id: z.number().int().positive().optional(),
  inventaire_id: z.number().int().positive().optional(),
  epaysage: EpaysageDataSchema,
  inventaire: InventaireDataSchema.optional(),
  client_request_id: z.string().max(128).optional(),
  creation_source: InventaireCreationSourceSchema.optional(),
});

export const EpaysageEssenceActionSchema = z.object({
  action: z.enum(["ADD", "EDIT"]),
  epaysage_id: z.number().int().positive(),
  essence: EssenceDataSchema,
  client_request_id: z.string().max(128).optional(),
});

export type Epaysage = z.infer<typeof EpaysageSchema>;
export type EpaysageCreateInput = z.infer<typeof EpaysageCreateSchema>;
export type EpaysageUpdateInput = z.infer<typeof EpaysageUpdateSchema>;
export type EpaysageSelectInput = z.infer<typeof EpaysageSelectSchema>;
export type EpaysageFormData = EpaysageCreateInput;
export type EpaysageEditFormData = Omit<EpaysageUpdateInput, "id">;
export type EpaysageDisplayItem = Pick<Epaysage, "id" | "address" | "ville" | "pays" | "area" | "type_espace">;
export type EpaysageSelectOption = Pick<Epaysage, "id" | "address" | "ville">;
export type EpaysageTableItem = Pick<Epaysage, "id" | "address" | "ville" | "pays" | "area" | "type_espace" | "created_at" | "updated_at">;
export type EpaysageResponse = z.infer<typeof EpaysageResponseSchema>;
export type EpaysageListResponse = z.infer<typeof EpaysageListResponseSchema>;
export type EpaysageCompleteInput = z.infer<typeof EpaysageCompleteInputSchema>;
export type EpaysageDataInput = z.infer<typeof EpaysageDataSchema>;
export type EssenceDataInput = z.infer<typeof EssenceDataSchema>;
export type EpaysageBaseSaveInput = z.infer<typeof EpaysageBaseSaveSchema>;
export type EpaysageEssenceActionInput = z.infer<typeof EpaysageEssenceActionSchema>;
export type EpaysageInventaireDataInput = z.infer<typeof InventaireDataSchema>;

export function validateEpaysageCreate(data: unknown) {
  return EpaysageCreateSchema.safeParse(data);
}

export function validateEpaysageUpdate(data: unknown) {
  return EpaysageUpdateSchema.safeParse(data);
}

export function validateEpaysageSelect(data: unknown) {
  return EpaysageSelectSchema.safeParse(data);
}

export function validateEpaysageComplete(data: unknown) {
  return EpaysageCompleteInputSchema.safeParse(data);
}

export function validateEpaysageBaseSave(data: unknown) {
  return EpaysageBaseSaveSchema.safeParse(data);
}

export function validateEpaysageEssenceAction(data: unknown) {
  return EpaysageEssenceActionSchema.safeParse(data);
}
